import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Activity, Plus } from "lucide-react";

export interface GoalTrackerXRecord {
  id: string;
  name: string;
  valueType: "number" | "weight" | "currency" | "time" | string;
  unitLabel: string | null;
  period: "daily" | "weekly" | "monthly" | string;
  goalValue: number | null;
  isActive: boolean;
  currentPeriodTotal: number;
}

const PERIOD_LABELS: Record<string, string> = {
  daily: "today",
  weekly: "this week",
  monthly: "this month",
};

function formatValue(value: number, valueType: string, unitLabel: string | null) {
  if (valueType === "currency") return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (valueType === "weight") return `${value}${unitLabel ?? "kg"}`;
  if (valueType === "time") return `${value} ${unitLabel ?? "minutes"}`;
  return `${value}${unitLabel ? ` ${unitLabel}` : ""}`;
}

function TrackerBranchRow({ tracker, goalId }: { tracker: GoalTrackerXRecord; goalId: string }) {
  const { toast } = useToast();
  const [value, setValue] = useState("");

  const logMutation = useMutation({
    mutationFn: (val: number) =>
      apiRequest("POST", `/api/trackerx/${tracker.id}/entries`, { value: val }),
    onSuccess: () => {
      setValue("");
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goalId, "trackerx"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trackerx"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trackerx/widget-metrics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trackerx/all-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trackerx/totals"] });
      queryClient.invalidateQueries({ predicate: (q) => /trackerx-summary|habit-summaries/.test(String(q.queryKey[0])) });
      toast({ title: "Logged", description: `Entry added to ${tracker.name}` });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to log entry", variant: "destructive" });
    },
  });

  const handleLog = () => {
    const num = Number(value);
    if (!value.trim() || Number.isNaN(num)) {
      toast({ title: "Enter a value", description: "Type a number to log", variant: "destructive" });
      return;
    }
    logMutation.mutate(num);
  };

  const periodLabel = PERIOD_LABELS[tracker.period] ?? tracker.period;
  const total = formatValue(tracker.currentPeriodTotal, tracker.valueType, tracker.unitLabel);
  const target = tracker.goalValue != null
    ? formatValue(tracker.goalValue, tracker.valueType, tracker.unitLabel)
    : null;

  return (
    <div
      className="relative pl-6 py-2"
      data-testid={`row-goal-trackerx-${tracker.id}`}
    >
      {/* branch connector */}
      <div className="absolute left-2 top-0 bottom-0 w-px bg-border" aria-hidden="true" />
      <div className="absolute left-2 top-1/2 w-4 h-px bg-border" aria-hidden="true" />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-muted/30 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Activity className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-foreground truncate" data-testid={`text-trackerx-name-${tracker.id}`}>
              {tracker.name}
            </div>
            <div className="text-xs text-muted-foreground" data-testid={`text-trackerx-total-${tracker.id}`}>
              {target ? (
                <>{total} of {target} {periodLabel}</>
              ) : (
                <>{total} {periodLabel}</>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Input
            type="number"
            inputMode="decimal"
            placeholder="Value"
            className="h-8 w-24"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleLog();
              }
            }}
            data-testid={`input-trackerx-log-${tracker.id}`}
          />
          <Button
            size="sm"
            className="h-8"
            onClick={handleLog}
            disabled={logMutation.isPending}
            data-testid={`button-trackerx-log-${tracker.id}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Log
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function GoalTrackerXBranch({ goalId, enabled }: { goalId: string; enabled: boolean }) {
  const { data: trackers = [], isLoading } = useQuery<GoalTrackerXRecord[]>({
    queryKey: ["/api/goals", goalId, "trackerx"],
    queryFn: async () => {
      const response = await fetch(`/api/goals/${goalId}/trackerx`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch trackers");
      return response.json();
    },
    enabled,
  });

  if (!enabled) return null;

  const activeTrackers = trackers.filter((t) => t.isActive);

  return (
    <div
      className="mt-2 cursor-default"
      onClick={(e) => e.stopPropagation()}
      data-testid={`section-goal-trackerx-${goalId}`}
    >
      {isLoading ? (
        <div className="text-sm text-muted-foreground py-3 text-center">Loading trackers...</div>
      ) : activeTrackers.length === 0 ? (
        <div className="text-sm text-muted-foreground py-3 text-center">
          No TrackerX records linked to this goal yet
        </div>
      ) : (
        activeTrackers.map((tracker) => (
          <TrackerBranchRow key={tracker.id} tracker={tracker} goalId={goalId} />
        ))
      )}
    </div>
  );
}
